'use client';
import Link from 'next/link';
import { useState, useEffect, useMemo } from 'react';
import { FiChevronLeft } from 'react-icons/fi';

import EmptyStarIcon from '../Icons/EmptyStar';
import FilledStarIcon from '../Icons/FilledStar';
import Button from '../Button';

import { Village } from '@/services/types';
import { WishList } from '../VillageCard/Options';
import { IFixedHeaderDictionary } from '@/dictionaries/components/fixedHeader/types';

import useLocalStorage from '@/hooks/useLocalStorage';
import { getVillageWishListInfos } from '@/utils/village';

import useVillageContext from '@/hooks/useVillageContext';
import useObserverContext from '@/hooks/useObserverContext';
import { useCurrency } from '@/contexts/currencyContext';
import { cn } from '@/lib/utils';
import { pugliaMap, sicilyMap, villagesMap } from '@/utils/maps';
import './styles.css';

type Props = {
  village: Village;
  fixedHeaderDict: IFixedHeaderDictionary;
  lang: string;
  villaInPuglia: boolean;
};

export default function FixedHeader({
  village,
  fixedHeaderDict,
  lang,
  villaInPuglia,
}: Props) {
  const {
    book,
    enquiry,
    addToShortlist,
    removeFromShortlist,
    requestToBook,
    ...sections
  } = fixedHeaderDict;

  const [wishList, setWishList] = useLocalStorage('villas', [] as WishList[]);
  const [fixHeader, setFixHeader] = useState(false);

  const { inWishList, handleWishList } = useVillageContext();
  const { currentSection } = useObserverContext();
  const { currency } = useCurrency();

  const wishListInfos = getVillageWishListInfos(village, currency.value);

  const sectionsArray = Object.entries(sections)
    .map(([id, label]) => ({
      id,
      label,
    }))
    .filter((section) => {
      if (villaInPuglia) {
        return section.id !== 'experiences' && section.id !== 'reviews';
      }
      return section;
    });

  function handleScrollToElement(elementId: string) {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  function handleVillageWishList() {
    let newWishList: WishList[] = [];

    if (!inWishList) {
      newWishList = [...wishList, wishListInfos];
    } else {
      newWishList = wishList.filter(
        (saved) => saved.title !== wishListInfos.title,
      );
    }
    setWishList(newWishList);
  }

  useEffect(() => {
    const handleScroll = () => {
      const fixedHeader = document.querySelector('#villages-medias-section');

      if (!fixedHeader) return;

      const mediaRect = fixedHeader.getBoundingClientRect();

      if (mediaRect.top <= 0) {
        setFixHeader(true);
      } else {
        setFixHeader(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fixHeader]);

  useEffect(() => {
    const isSaved = wishList.some(
      (savedVillage) => savedVillage.title === village.title,
    );

    if (isSaved) {
      handleWishList(true);
    } else {
      handleWishList(false);
    }
  }, [handleWishList, village.title, wishList]);

  useEffect(() => {
    const hash = window.location.hash.substring(1);

    if (hash) {
      handleScrollToElement(hash);
    }
  }, []);

  const backHref = useMemo(() => {
    if (villaInPuglia) {
      return lang === 'en'
        ? `/${pugliaMap[lang]}/${villagesMap[lang]}`
        : `/${lang}/${pugliaMap[lang]}/${villagesMap[lang]}`;
    }

    return lang === 'en'
      ? `/${sicilyMap[lang]}/${villagesMap[lang]}`
      : `/${lang}/${sicilyMap[lang]}/${villagesMap[lang]}`;
  }, [lang, villaInPuglia]);

  return (
    <div
      id="fixed-header"
      className={cn(
        'px-sm md:px-md max-w-default desktop:max-w-full flex items-center w-full shadow-[0_10px_20px_0_rgba(0,0,0,0.1)] h-[72px] bg-white fixed right-0 top-[-72px] z-[999] transition-all duration-500 [&.is-sticky]:translate-y-[72px]',
        'lg:h-[124px] lg:top-[-124px] lg:[&.is-sticky]:translate-y-[124px]',
        { 'translate-y-[72px] lg:translate-y-[124px]': fixHeader },
      )}>
      <div className="flex-[1_0_auto] flex">
        <Link
          aria-label="Back to villages catalog"
          className="self-start mr-2 whitespace-nowrap cursor-pointer"
          href={backHref}>
          <div className="flex">
            <FiChevronLeft className="w-6 h-6 text-gray-500" />
          </div>
        </Link>
        <nav className="hidden desktop:flex ml-2">
          <ul className="w-auto flex justify-between overflow-hidden mx-[10px] list-none">
            {sectionsArray.map(({ id, label }) => (
              <li
                key={id}
                className="mr-[15px] first:pl-[10px] whitespace-nowrap">
                <span
                  onClick={() => handleScrollToElement(`${id}-ref`)}
                  className={cn('header-link cursor-pointer', {
                    'header-link-active': currentSection.startsWith(id),
                  })}>
                  {label}
                </span>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <div className="flex-[1_0_auto] flex justify-end items-center ml-auto">
        <div
          onClick={handleVillageWishList}
          className="mr-2 whitespace-nowrap cursor-pointer">
          <div className="cursor-pointer flex flex-wrap items-center content-center justify-center">
            {inWishList ? (
              <FilledStarIcon className="w-6 h-6 fill-current text-primary" />
            ) : (
              <EmptyStarIcon className=" w-6 h-6 fill-current text-primary" />
            )}
            <span className="hidden md:block ml-2 text-primary">
              {inWishList ? removeFromShortlist : addToShortlist}
            </span>
          </div>
        </div>

        <div>
          <Button
            onClick={() => handleScrollToElement('enquiry-form')}
            variant="outline"
            type="button"
            className="ml-2 w-fit h-[50px] py-[16px] px-[20px]">
            {villaInPuglia ? requestToBook : enquiry}
          </Button>
        </div>
        <div
          className={cn('hidden desktop:block', {
            'desktop:hidden': villaInPuglia,
          })}>
          <Button
            onClick={() => handleScrollToElement('book-section')}
            type="button"
            className="ml-2 w-fit h-[50px] py-[16px] px-[20px]">
            {book}
          </Button>
        </div>
      </div>
    </div>
  );
}
